import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductListColumnsDrawer from "@insite/content-library/Widgets/ProductList/ProductListColumnsDrawer";
import Button from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FilterIcon from "@tJohnstonCompanies/Icons/FilterIcon";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    view: state.pages.productList.view,
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
    brandFacets: getProductListDataViewProperty(state, "brandFacets"),
    productLineFacets: getProductListDataViewProperty(state, "productLineFacets"),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListTableViewFilterStyles {
    wrapper?: InjectableCss;
    filterButton?: InjectableCss;
    filterText?: InjectableCss;
    filterDrawer?: DrawerPresentationProps;
    CustomfilterIcon?: IconPresentationProps;
    filterDrawerContentWrapper?: InjectableCss;
    filterDrawerButtonWrapper?: InjectableCss;
    filterButtonInDrawer: InjectableCss;
}

export const viewSelectStyles: ProductListTableViewFilterStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
        `,
    },
    filterButton: {
        css: css`
            display: flex;
            align-items: center;
            background-color: #444444;
            color: white;
            padding: 10px 18px;
            border-radius: 5px;
            cursor: pointer;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        justify-content: center;
                    `,
                    css`
                        width: 100%;
                        justify-content: center;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    filterDrawer: {
        size: 350,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
                padding: 12px;
                scrollbar-width: thin;
                scrollbar-color: silver transparent;
            `,
            drawerTitle: css`
                background: #f0f0f0;
                color: #333 !important;
                h4 {
                    margin: 7px 0 7px 20px;
                    font-size: 21px !important;
                    font-family: "Roboto", "Open Sans", "Lato", sans-serif !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                margin: 12px 0 7px 20px;
                                font-size: 25px !important;
                            `,
                            css`
                                margin: 12px 0 7px 20px;
                                font-size: 25px !important;
                            `,
                            css`
                                margin: 12px 0 7px 20px;
                                font-size: 25px !important;
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            titleButton: css`
                background: #f0f0f0;
                border: none;
                span[class^="IconWrapper"] {
                    color: #333 !important;
                    background: none !important;
                    border-radius: 0 !important;
                    border: none !important;
                }
                &:hover {
                    background: #f0f0f0;
                    border: none;
                }
            `,
            drawerBody: css`
                background: #f0f0f0;
            `,
        },
    },
    filterText: {
        css: css`
            font-size: 16px;
            font-weight: 700;
        `,
    },
    CustomfilterIcon: {
        css: css`
            margin-right: 10px;
            width: 24px;
            height: 24px;
        `,
    },
    filterDrawerContentWrapper: {
        css: css`
            padding-bottom: 60px;
            > div {
                > dl {
                    padding-top: 0px !important;
                    > div {
                        > dd {
                            background: #fff;
                            > div {
                                background: #fff !important;
                            }
                        }
                    }
                }
            }
        `,
    },
    filterDrawerButtonWrapper: {
        css: css`
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
        `,
    },
    filterButtonInDrawer: {
        css: css`
            width: calc(100% - 20px);
            margin: 10px;
            border: none;
            cursor: pointer;
            border-radius: 0;
            text-align: center;
            height: 60px;
            span {
                padding: 15px;
                font-size: 18px;
                color: white;
            }
        `,
    },
};

const styles = viewSelectStyles;

const ProductListTableViewFilter = ({ id, loaded, pagination }: Props) => {
    const [filterDrawerIsOpen, setFilterDrawerIsOpen] = useState(false);

    if (!loaded) {
        return null;
    }

    const clickFilterHandler = () => {
        setFilterDrawerIsOpen(!filterDrawerIsOpen);
    };

    const filterDrawerCloseHandler = () => {
        setFilterDrawerIsOpen(false);
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper
                    {...styles.filterButton}
                    onClick={clickFilterHandler}
                    data-test-selector="productListFilterButton"
                >
                    <Icon src={FilterIcon} {...styles.CustomfilterIcon} />
                    <Typography {...styles.filterText}>{translate("Filters")}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            <Drawer
                position="left"
                {...(styles.filterDrawer as DrawerProps)}
                isOpen={filterDrawerIsOpen}
                handleClose={filterDrawerCloseHandler}
                data-test-selector="productListFilterDrawer"
                headline="Filter"
            >
                <StyledWrapper {...styles.filterDrawerContentWrapper}>
                    <Zone zoneName="Container" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.filterDrawerButtonWrapper}>
                    <Button {...styles.filterButtonInDrawer} onClick={filterDrawerCloseHandler}>
                        {translate(`See ${pagination?.totalItemCount.toLocaleString()} results`)}
                    </Button>
                </StyledWrapper>
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListTableViewFilter),
    definition: {
        group: "Product List",
        displayName: "Table View Filter",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
